import React from "react";
import { getFiles } from "../api/files";
import { handleError } from "./handleError";
import { SelectedFile } from "../types/PostSelection";

// Module-level cache for in-memory storage
let fileCache: {
  files: SelectedFile[];
  pageNumber: number;
  hasMore: boolean;
  lastUpdated: number;
  folderPath: string;
} | null = null;

// Queue system for managing file loading requests
let loadQueue: number[] = [];
let isProcessingQueue = false;
let loadedPages = new Set<number>();
let currentFolderPath = "";

// Cache utility functions
const saveToCache = (
  files: SelectedFile[],
  pageNumber: number,
  hasMore: boolean,
  folderPath: string = ""
) => {
  const cacheData = {
    files,
    pageNumber,
    hasMore,
    folderPath,
    lastUpdated: Date.now(),
  };

  // Save to module-level cache
  fileCache = cacheData;
  console.log(
    `📦 Cache updated: ${files.length} files, page ${pageNumber}, hasMore: ${hasMore}, folder: ${folderPath}`
  );
};

const loadFromCache = (): typeof fileCache => {
  // Check module-level cache
  if (fileCache) {
    console.log("📦 Using in-memory cache:", fileCache.files.length, "files");
    return fileCache;
  }

  console.log("📦 No valid cache found, will fetch fresh data");
  return null;
};

// Queue processing function
const processLoadQueue = async (
  setFiles: React.Dispatch<React.SetStateAction<SelectedFile[]>>,
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setPageNumber: React.Dispatch<React.SetStateAction<number>>,
  setInitialLoading: React.Dispatch<React.SetStateAction<boolean>>,
  folderPath: string = ""
) => {
  if (isProcessingQueue || loadQueue.length === 0) return;

  isProcessingQueue = true;
  setIsLoading(true);

  while (loadQueue.length > 0) {
    const page = loadQueue.shift()!;

    // Skip if page already loaded
    if (loadedPages.has(page)) {
      console.log(`📦 Page ${page} already loaded, skipping`);
      continue;
    }

    try {
      console.log(`📦 Loading files for page: ${page}, folder: ${folderPath}`);
      const newFiles = await getFiles(page, 25, folderPath);
      loadedPages.add(page);
      setPageNumber(page);

      if (newFiles.length === 0) {
        setHasMore(false);
        setFiles((currentFiles) => {
          saveToCache(currentFiles, page, false, folderPath);
          return currentFiles;
        });
        break; // No more files, stop processing
      } else {
        setFiles((prev) => {
          const updatedFiles = [...prev, ...newFiles];
          saveToCache(updatedFiles, page, true, folderPath);
          return updatedFiles;
        });
      }
    } catch (err: any) {
      console.error(`📦 Error loading page ${page}:`, err);
      handleError(err);
      // Remove from loaded pages so it can be retried
      loadedPages.delete(page);
    }
  }

  isProcessingQueue = false;
  setIsLoading(false);
  setInitialLoading(false);
};

// Function to add page to queue
const queuePageLoad = (
  page: number,
  setFiles: React.Dispatch<React.SetStateAction<SelectedFile[]>>,
  setHasMore: React.Dispatch<React.SetStateAction<boolean>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setPageNumber: React.Dispatch<React.SetStateAction<number>>,
  setInitialLoading: React.Dispatch<React.SetStateAction<boolean>>,
  folderPath: string = ""
) => {
  // Clear cache and reset if folder changed
  if (currentFolderPath !== folderPath) {
    loadQueue = [];
    loadedPages.clear();
    isProcessingQueue = false;
    currentFolderPath = folderPath;
  }

  // Don't queue if page already loaded or already in queue
  if (loadedPages.has(page) || loadQueue.includes(page)) {
    console.log(`📦 Page ${page} already loaded or queued, skipping`);
    setInitialLoading(false);
    return;
  }

  console.log(`📦 Queueing page ${page} for loading in folder: ${folderPath}`);
  loadQueue.push(page);

  // Start processing if not already processing
  if (!isProcessingQueue) {
    processLoadQueue(
      setFiles,
      setHasMore,
      setIsLoading,
      setPageNumber,
      setInitialLoading,
      folderPath
    );
  }
};

// Function to clear queue and reset tracking (useful for cache clearing)
const clearLoadQueue = () => {
  loadQueue = [];
  loadedPages.clear();
  isProcessingQueue = false;
  console.log("📦 Load queue cleared");
};

const clearCache = () => {
  fileCache = null;
  clearLoadQueue();
  console.log("📦 Cache cleared");
};

// Function to mark pages as loaded (used when restoring from cache)
const markPagesAsLoaded = (maxPageNumber: number) => {
  for (let i = 0; i <= maxPageNumber; i++) {
    loadedPages.add(i);
  }
};

// Export all public functions
export {
  saveToCache,
  loadFromCache,
  queuePageLoad,
  clearCache,
  markPagesAsLoaded,
};
